// Pointer drag of a board card: the ghost follows the pointer outside React, the place under the pointer is found once per frame,
// columns and the board scroll at their edges. A mouse starts after a few pixels of movement, a finger after a long press.

import { type Id } from '@confdeck/core-shared';
import { sortedIndex } from './order.ts';

/** Distance from a column's edge where a drag scrolls it, and the largest step per frame (at the very edge). */
const EDGE = 48;
const STEP = 14;
/** A finger holds this long on a card before it lifts; a move farther than SLOP before that is a scroll, not a drag. */
const LONG_PRESS = 350;
const SLOP = 8;

export interface Place { over: string | null; index: number | null }

export interface DragHost {
  board: HTMLElement | null;
  ghost: () => HTMLElement | null;
  /** Counts the renders of the board: a drag re-reads its cached layout after one (decision 031 п.6, review P2.3). */
  rev: () => number;
  buckets: Map<string, Id[]>;
  ids: Id[];
  sorted: boolean;
  none: string;
  onStart: (place: Place, rect: DOMRect, at: { x: number; y: number }) => void;
  onPlace: (place: Place) => void;
  onDrop: (place: Place, ghost: DOMRect | null) => void;
  onCancel: () => void;
  /** The gesture is over (dropped, cancelled, a scroll took it, or the board went away): its window listeners are gone. */
  onEnd: () => void;
}

/** A card of a column in the coordinates of the column's content. */
export interface Box { top: number; height: number }

/**
 * Where a card lands in a column: the number of the other cards whose middle is above `y` (the middle of the card in the
 * hand). The open insert line, when it stands before a card, is taken out of that card's place, so the answer does not
 * depend on where the line is drawn now. `gap` is the column's row gap that comes with the line.
 */
export function placeIndex(cards: Box[], y: number, insert: Box | null = null, gap = 0): number {
  const shift = insert ? insert.height + gap : 0;
  let n = 0;
  for (const c of cards) if (c.top - (insert && insert.top < c.top ? shift : 0) + c.height / 2 < y) n++;
  return n;
}

/**
 * The scroll step near an edge: it grows the deeper the pointer is in the edge zone, so a finger at the side of a phone
 * moves the board column by column instead of flying past them.
 */
export function edgeStep(at: number, start: number, end: number): number {
  if (at < start + EDGE) return -Math.max(1, Math.round(STEP * Math.min(1, (start + EDGE - at) / EDGE)));
  if (at > end - EDGE) return Math.max(1, Math.round(STEP * Math.min(1, (at - end + EDGE) / EDGE)));
  return 0;
}

export const ghostTransform = (p: { x: number; y: number }) => `translate3d(${p.x}px, ${p.y}px, 0)`;

/**
 * Drag by window listeners: moves and the release are seen wherever the pointer goes. Layout is read once per gesture,
 * not on every move: column bounds and the cards' boxes in the column's own coordinates; a scroll outside the columns and a
 * render of the board during the drag mark the cache stale. Returns a stop that removes the listeners (the board unmounts).
 */
export function startPointerDrag(e: React.PointerEvent, id: Id, host: DragHost): () => void {
  const card = e.currentTarget as HTMLElement;
  const rect = card.getBoundingClientRect();
  const from = (card.parentElement as HTMLElement).dataset.cards ?? host.none;
  const touch = e.pointerType === 'touch';
  const x0 = e.clientX, y0 = e.clientY;
  const dx = x0 - rect.left, dy = y0 - rect.top;
  let started = false;
  let place: Place = { over: null, index: null };
  let pointer = { x: x0, y: y0 };
  let frame = 0;
  const position = new Map(host.ids.map((x, i) => [x, i]));

  type Col = { el: HTMLElement; left: number; right: number; top: number; bottom: number };
  let cache: { board: DOMRect | null; cols: Col[]; boxes: Map<HTMLElement, { cards: Box[]; insert: Box | null; gap: number }> } | null = null;
  let seen = host.rev();
  const layout = () => {
    if (!cache || seen !== host.rev()) {
      seen = host.rev();
      cache = {
        board: host.board?.getBoundingClientRect() ?? null,
        cols: [...(host.board?.querySelectorAll<HTMLElement>('[data-cards]') ?? [])].map((el) => {
          const r = (el.parentElement as HTMLElement).getBoundingClientRect();
          const own = el.getBoundingClientRect();
          return { el, left: r.left, right: r.right, top: own.top + el.clientTop, bottom: own.bottom };
        }),
        boxes: new Map(),
      };
    }
    return cache;
  };
  const stale = () => { cache = null; };
  const columnAt = (x: number): HTMLElement | null => {
    let best: { el: HTMLElement; d: number } | null = null;
    for (const c of layout().cols) {
      const d = x < c.left ? c.left - x : x > c.right ? x - c.right : 0;
      if (!best || d < best.d) best = { el: c.el, d };
    }
    return best && best.d < 80 ? best.el : null;
  };
  /** The column's other cards and its open insert line, in the coordinates of its content. */
  const boxes = (el: HTMLElement) => {
    const known = layout().boxes.get(el);
    if (known) return known;
    const line = el.querySelector<HTMLElement>(':scope > [data-insert]');
    const v = {
      cards: [...el.querySelectorAll<HTMLElement>(':scope > [data-id]')].filter((c) => c.dataset.id !== id).map((c) => ({ top: c.offsetTop, height: c.offsetHeight })),
      insert: line ? { top: line.offsetTop, height: line.offsetHeight } : null,
      gap: parseFloat(getComputedStyle(el).rowGap || '0') || 0,
    };
    layout().boxes.set(el, v);
    return v;
  };
  /** The index among the column's cards (without the dragged one) where the middle of the card in the hand is. */
  const indexAt = (el: HTMLElement, y: number): number => {
    const col = el.dataset.cards!;
    if (host.sorted) return sortedIndex(host.buckets.get(col) ?? [], id, (x) => position.get(x) ?? 0);
    const top = layout().cols.find((c) => c.el === el)?.top ?? 0;
    const b = boxes(el);
    return placeIndex(b.cards, y - top + el.scrollTop, b.insert, b.gap);
  };
  const update = () => {
    const el = columnAt(pointer.x);
    const over = el?.dataset.cards ?? null;
    // The place follows the middle of the card in the hand: the card lands where it is seen under the pointer.
    const index = el && over !== host.none ? indexAt(el, pointer.y - dy + rect.height / 2) : null;
    if (over !== place.over || index !== place.index) { place = { over, index }; host.onPlace(place); }
  };
  /** One frame per batch of moves: the place is found, and near an edge the column scrolls and the board sideways. */
  const step = () => {
    frame = 0;
    if (!started) return;
    update();
    let moved = false;
    const { board: r, cols } = layout();
    const board = host.board;
    if (board && r) {
      const sx = edgeStep(pointer.x, r.left, r.right);
      if (sx && board.scrollWidth > board.clientWidth) { board.scrollLeft += sx; moved = true; }
    }
    const c = place.over ? cols.find((x) => x.el.dataset.cards === place.over) : null;
    if (c) {
      const sy = edgeStep(pointer.y, c.top, c.bottom);
      if (sy && c.el.scrollHeight > c.el.clientHeight) { c.el.scrollTop += sy; moved = true; }
    }
    if (moved) frame = requestAnimationFrame(step);
  };
  const ghostAt = () => ({ x: pointer.x - dx, y: pointer.y - dy });
  const moveGhost = () => { const g = host.ghost(); if (g) g.style.transform = ghostTransform(ghostAt()); };
  const begin = () => {
    started = true;
    const startIndex = (host.buckets.get(from) ?? []).indexOf(id);
    place = { over: from, index: startIndex < 0 ? null : startIndex };
    host.onStart(place, rect, ghostAt());
    if (touch) navigator.vibrate?.(8);
  };
  // Any scroll but a column's own (its cards are kept in content coordinates) moves the columns: the page, the board, an outer block.
  const onScroll = (ev: Event) => { if (!(ev.target instanceof HTMLElement && ev.target.dataset.cards !== undefined)) stale(); };
  // Once a finger has lifted the card, its moves drag the card and no longer scroll the column under it.
  const holdTouch = (ev: TouchEvent) => { if (started && ev.cancelable) ev.preventDefault(); };
  const noMenu = (ev: Event) => ev.preventDefault();
  const press = touch ? window.setTimeout(begin, LONG_PRESS) : 0;
  const end = () => {
    clearTimeout(press);
    host.onEnd();
    window.removeEventListener('pointermove', mv);
    window.removeEventListener('pointerup', up);
    window.removeEventListener('pointercancel', cancel);
    window.removeEventListener('scroll', onScroll, true);
    window.removeEventListener('resize', stale);
    window.removeEventListener('touchmove', holdTouch);
    window.removeEventListener('contextmenu', noMenu, true);
    cancelAnimationFrame(frame);
  };
  const mv = (ev: PointerEvent) => {
    if (ev.pointerId !== e.pointerId) return;
    pointer = { x: ev.clientX, y: ev.clientY };
    const far = Math.hypot(ev.clientX - x0, ev.clientY - y0);
    if (!started) {
      if (touch) { if (far > SLOP) end(); return; }
      if (far < 5) return;
      begin();
      return;
    }
    moveGhost();
    if (!frame) frame = requestAnimationFrame(step);
  };
  const up = (ev: PointerEvent) => {
    if (ev.pointerId !== e.pointerId) return;
    end();
    if (!started) return;
    // A release between frames writes the place of the last pointer position, not of the frame before.
    update();
    // The landing starts from the card as it is seen: the inner, tilted card of the ghost.
    const g = host.ghost();
    host.onDrop(place, (g?.firstElementChild ?? g)?.getBoundingClientRect() ?? null);
    // Swallow the click that follows a drag so the card does not open. A drop elsewhere produces no click, so the trap
    // also goes with the next press: it never eats a later real click (a dialog's button), whenever the browser sends it.
    const swallow = (ce: MouseEvent) => { ce.stopPropagation(); untrap(); };
    const untrap = () => { window.removeEventListener('click', swallow, true); window.removeEventListener('pointerdown', untrap, true); };
    window.addEventListener('click', swallow, true);
    window.addEventListener('pointerdown', untrap, true);
  };
  const cancel = (ev: PointerEvent) => { if (ev.pointerId === e.pointerId) { end(); if (started) host.onCancel(); } };
  window.addEventListener('pointermove', mv);
  window.addEventListener('pointerup', up);
  window.addEventListener('pointercancel', cancel);
  window.addEventListener('scroll', onScroll, { capture: true, passive: true });
  window.addEventListener('resize', stale, { passive: true });
  window.addEventListener('touchmove', holdTouch, { passive: false });
  if (touch) window.addEventListener('contextmenu', noMenu, true);
  return end;
}
